import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar, MapPin, Gift, Phone, MessageSquare, ExternalLink, Facebook, Instagram, Twitter, BookOpen, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Animated Sound Bars Component
function SoundBars({ isPlaying, color = '#9333ea' }) {
  return (
    <div className="flex items-end gap-[2px] h-4">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="w-[3px] rounded-full transition-all"
          style={{
            backgroundColor: color,
            height: isPlaying ? undefined : '4px',
            animation: isPlaying ? `soundBar ${0.4 + i * 0.1}s ease-in-out infinite alternate` : 'none',
            animationDelay: `${i * 0.1}s`
          }}
        />
      ))}
      <style>{`
        @keyframes soundBar {
          0% { height: 4px; }
          100% { height: 16px; }
        }
      `}</style>
    </div>
  );
}

// Countdown component
function Countdown({ targetDate, targetTime, colors }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  useEffect(() => {
    const target = new Date(`${targetDate}T${targetTime || '00:00'}`);
    
    const interval = setInterval(() => {
      const now = new Date();
      const diff = target - now;
      
      if (diff > 0) {
        setTimeLeft({
          days: Math.floor(diff / (1000 * 60 * 60 * 24)),
          hours: Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((diff % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [targetDate, targetTime]);

  return (
    <div className="text-center py-4">
      <p className="text-xs tracking-widest mb-3 opacity-80">MENANTI HARI BAHAGIA</p>
      <div className="flex justify-center gap-2">
        {[
          { value: timeLeft.days, label: 'HARI' },
          { value: timeLeft.hours, label: 'JAM' },
          { value: timeLeft.minutes, label: 'MINIT' },
          { value: timeLeft.seconds, label: 'SAAT' }
        ].map((item, idx) => (
          <div 
            key={idx} 
            className="backdrop-blur-sm rounded-lg p-2 min-w-[50px]"
            style={{ backgroundColor: colors?.countdown_bg || 'rgba(255,255,255,0.1)' }}
          >
            <div className="text-xl font-bold" style={{ color: colors?.countdown_text || '#FFFFFF' }}>
              {String(item.value).padStart(2, '0')}
            </div>
            <div className="text-[8px] tracking-wider">{item.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function PublicCard() {
  const { slug } = useParams();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isOpened, setIsOpened] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [youtubePlayer, setYoutubePlayer] = useState(null);
  const [seoTitleTemplate, setSeoTitleTemplate] = useState('{bride} & {groom} | Kad Kahwin Digital');
  const [galleryIndex, setGalleryIndex] = useState(0); // For gallery carousel
  
  // Dialogs
  const [showRSVP, setShowRSVP] = useState(false);
  const [showGuestbook, setShowGuestbook] = useState(false);
  const [showGift, setShowGift] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [showLocation, setShowLocation] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  
  // RSVP states - New flow: name first, then attendance choice, then details
  const [rsvpStep, setRsvpStep] = useState('name'); // 'name', 'choice', 'form', 'notAttending', 'done'
  const [rsvpAttending, setRsvpAttending] = useState(null);
  const [rsvpSubmitting, setRsvpSubmitting] = useState(false);
  const [rsvpForm, setRsvpForm] = useState({ name: '', phone: '', num_guests: 1, wish_message: '' });
  
  // Guestbook states
  const [guestbookSubmitting, setGuestbookSubmitting] = useState(false);
  const [guestbookForm, setGuestbookForm] = useState({ name: '', message: '' });
  const [guestbookEntries, setGuestbookEntries] = useState([]);
  
  const [copied, setCopied] = useState(false);
  const audioRef = useRef(null);

  // Helper function to check if URL is a valid audio URL
  const isValidAudioUrl = (url) => {
    if (!url) return false;
    // Accept direct audio files and base64 audio
    return url.startsWith('data:audio/') || 
           url.endsWith('.mp3') || 
           url.endsWith('.wav') || 
           url.endsWith('.ogg') ||
           url.endsWith('.m4a') ||
           url.endsWith('.aac') ||
           url.includes('audio');
  };

  useEffect(() => {
    fetchCard();
    fetchSeoTemplate();
  }, [slug]);

  const fetchSeoTemplate = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/landing-settings`);
      if (res.data?.card_seo_title_template) {
        setSeoTitleTemplate(res.data.card_seo_title_template);
      }
    } catch (e) {
      // Use default
    }
  };

  const fetchCard = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/${slug}`);
      setCard(response.data);
      
      // Fetch guestbook entries for carousel
      try {
        const guestbookRes = await axios.get(`${BACKEND_URL}/api/public/${slug}/guestbook`);
        setGuestbookEntries(guestbookRes.data || []);
      } catch (e) {
        // Guestbook might not exist yet
      }
    } catch (error) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  // Generate page title from template
  const getPageTitle = () => {
    if (!card) return 'Kad Kahwin Digital';
    return seoTitleTemplate
      .replace('{bride}', card.bride_name || '')
      .replace('{groom}', card.groom_name || '');
  };

  const handleOpen = () => {
    // Start transition animation
    setIsTransitioning(true);
    
    // Try to start audio immediately on user interaction (required for mobile)
    if (card?.audio_url && audioRef.current) {
      audioRef.current.play().then(() => {
        setAudioPlaying(true);
      }).catch((err) => {
        console.log('Autoplay blocked, user can tap sound bar to play:', err);
      });
    }
    
    // After transition animation completes, show the main card
    setTimeout(() => {
      setIsOpened(true);
      setIsTransitioning(false);
    }, 800);
  };

  const toggleAudio = () => {
    if (audioRef.current) {
      if (audioPlaying) {
        audioRef.current.pause();
        setAudioPlaying(false);
      } else {
        audioRef.current.play().then(() => {
          setAudioPlaying(true);
        }).catch((err) => {
          console.log('Play failed:', err);
          toast.error('Sila tekan sekali lagi untuk mainkan muzik');
        });
      }
    }
  };

  // RSVP handlers - New flow: name first
  const handleRSVPNameSubmit = () => {
    if (!rsvpForm.name.trim()) {
      toast.error('Sila masukkan nama anda');
      return;
    }
    setRsvpStep('choice');
  };

  const handleRSVPAttendance = (attending) => {
    setRsvpAttending(attending);
    if (attending) {
      setRsvpStep('form');
    } else {
      // Go to not attending step - can add wish message
      setRsvpStep('notAttending');
    }
  };

  const submitRSVP = async (attending) => {
    setRsvpSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/rsvp/${slug}`, {
        name: rsvpForm.name || 'Tetamu',
        phone: rsvpForm.phone || '-',
        num_guests: attending ? rsvpForm.num_guests : 0,
        attending: attending,
        wish_message: rsvpForm.wish_message || ''
      });
      toast.success(attending ? 'Terima kasih! RSVP anda telah dihantar' : 'Terima kasih atas maklum balas anda');
      setRsvpStep('done');
      setTimeout(() => {
        setShowRSVP(false);
        setRsvpStep('name');
        setRsvpForm({ name: '', phone: '', num_guests: 1, wish_message: '' });
        setRsvpAttending(null);
      }, 2000);
    } catch (error) {
      toast.error('Gagal menghantar. Sila cuba lagi.');
    } finally {
      setRsvpSubmitting(false);
    }
  };

  const handleRSVPSubmit = async (e) => {
    e.preventDefault();
    if (!rsvpForm.phone) {
      toast.error('Sila isi nombor telefon');
      return;
    }
    await submitRSVP(true);
  };

  const handleNotAttendingSubmit = async (e) => {
    e.preventDefault();
    await submitRSVP(false);
  };

  // Guestbook handlers
  const handleGuestbookSubmit = async (e) => {
    e.preventDefault();
    if (!guestbookForm.name || !guestbookForm.message) {
      toast.error('Sila isi nama dan ucapan anda');
      return;
    }
    
    setGuestbookSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/guestbook/${slug}`, guestbookForm);
      toast.success('Terima kasih! Ucapan anda telah dihantar');
      setShowGuestbook(false);
      setGuestbookForm({ name: '', message: '' });
      
      // Refresh guestbook entries for carousel
      const guestbookRes = await axios.get(`${BACKEND_URL}/api/public/${slug}/guestbook`);
      setGuestbookEntries(guestbookRes.data || []);
    } catch (error) {
      toast.error('Gagal menghantar. Sila cuba lagi.');
    } finally {
      setGuestbookSubmitting(false);
    }
  };

  const handleAddToCalendar = (type) => {
    const startTime = card.event_start_time || card.wedding_time;
    const eventDate = new Date(`${card.wedding_date}T${startTime}`);
    const endDate = card.event_end_time 
      ? new Date(`${card.wedding_date}T${card.event_end_time}`)
      : new Date(eventDate.getTime() + 4 * 60 * 60 * 1000);
    
    const formatDate = (date) => date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';

    const eventTitle = `Walimatul Urus ${card.bride_name} & ${card.groom_name}`;
    const eventDetails = `Majlis Perkahwinan`;
    const eventLocation = card.venue_address;

    if (type === 'google') {
      const calendarUrl = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(eventTitle)}&dates=${formatDate(eventDate)}/${formatDate(endDate)}&details=${encodeURIComponent(eventDetails)}&location=${encodeURIComponent(eventLocation)}`;
      window.open(calendarUrl, '_blank');
    } else {
      const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//Kawkad//Wedding Card//EN
BEGIN:VEVENT
DTSTART:${formatDate(eventDate)}
DTEND:${formatDate(endDate)}
SUMMARY:${eventTitle}
DESCRIPTION:${eventDetails}
LOCATION:${eventLocation}
END:VEVENT
END:VCALENDAR`;
      
      const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${card.slug}-wedding.ics`;
      link.click();
    }
    setShowCalendar(false);
  };

  const handleOpenMap = (type) => {
    const address = encodeURIComponent(card.venue_address);
    let mapUrl;
    
    if (type === 'waze') {
      mapUrl = card.venue_lat && card.venue_lng 
        ? `https://waze.com/ul?ll=${card.venue_lat},${card.venue_lng}&navigate=yes`
        : `https://waze.com/ul?q=${address}`;
    } else {
      mapUrl = card.venue_map_url 
        || (card.venue_lat && card.venue_lng 
          ? `https://www.google.com/maps/dir/?api=1&destination=${card.venue_lat},${card.venue_lng}`
          : `https://www.google.com/maps/search/?api=1&query=${address}`);
    }
    window.open(mapUrl, '_blank');
    setShowLocation(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const openWhatsApp = (phone, name) => {
    const cleanPhone = phone.replace(/[^0-9]/g, '');
    const message = encodeURIComponent(`Assalamualaikum ${name}, saya ingin bertanya tentang majlis perkahwinan.`);
    window.open(`https://wa.me/${cleanPhone}?text=${message}`, '_blank');
  };

  const getSocialIcon = (platform) => {
    switch (platform.toLowerCase()) {
      case 'facebook': return <Facebook className="w-5 h-5" />;
      case 'instagram': return <Instagram className="w-5 h-5" />;
      case 'twitter': return <Twitter className="w-5 h-5" />;
      case 'tiktok': return (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
        </svg>
      );
      default: return <ExternalLink className="w-5 h-5" />;
    }
  };

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const period = h >= 12 ? 'Petang' : 'Pagi';
    const displayHour = h > 12 ? h - 12 : h;
    return `${displayHour}:${minutes} ${period}`;
  };

  // Get icon visibility settings
  const iconVisibility = card?.icon_visibility || { rsvp: true, gift: true, contact: true, location: true, calendar: true };

  // Get the parent names to display based on display_parent setting
  // Options: "bride" (show bride's parents first), "groom" (show groom's parents first), "both" (show both)
  const getDisplayParentName = () => {
    if (card?.display_parent === 'groom') {
      return card?.groom_parents;
    }
    if (card?.display_parent === 'both') {
      return card?.bride_parents; // First parent (bride)
    }
    return card?.bride_parents;
  };

  // Get the other parent name
  const getOtherParentName = () => {
    if (card?.display_parent === 'groom') {
      return card?.bride_parents;
    }
    if (card?.display_parent === 'both') {
      return card?.groom_parents; // Second parent (groom)
    }
    // For 'bride' option, don't show groom's parents (only bride's)
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Memuatkan...</p>
        </div>
      </div>
    );
  }

  if (error || !card) {
    return (
      <div className="min-h-screen bg-amber-50 flex items-center justify-center p-4">
        <div className="text-center" data-testid="card-not-found">
          <h1 className="text-4xl font-playfair font-bold text-gray-900 mb-4">Kad Tidak Dijumpai</h1>
          <p className="text-gray-600 mb-6">Maaf, kad jemputan yang anda cari tidak wujud.</p>
          <Button onClick={() => window.location.href = '/'} className="bg-emerald-900 text-white hover:bg-emerald-800 rounded-full px-8">
            Kembali ke Laman Utama
          </Button>
        </div>
      </div>
    );
  }

  // Font mapping for all available fonts
  const getFontClass = (fontStyle) => {
    const fontMap = {
      'playfair': "'Playfair Display', serif",
      'greatvibes': "'Great Vibes', cursive",
      'dancing': "'Dancing Script', cursive",
      'pinyon': "'Pinyon Script', cursive",
      'tangerine': "'Tangerine', cursive",
      'alex': "'Alex Brush', cursive",
      'allura': "'Allura', cursive",
      'sacramento': "'Sacramento', cursive",
      'cormorant': "'Cormorant Garamond', serif",
      'cinzel': "'Cinzel', serif",
      'manrope': "'Manrope', sans-serif",
      'lora': "'Lora', serif",
      'italiana': "'Italiana', serif",
    };
    return fontMap[fontStyle] || fontMap['playfair'];
  };

  const fontFamily = getFontClass(card.font_style);
  const startTime = card.event_start_time || card.wedding_time;
  const displayParent = getDisplayParentName();
  const otherParent = getOtherParentName();

  // Intro screen with transition animation
  // Mobile: Full screen | Desktop: Narrow centered card with 5px margin
  // IMPORTANT: Use single return with conditional rendering to prevent audio unmounting
  return (
    <>
      {/* Audio Element - ALWAYS rendered to prevent unmounting during transition */}
      {card.audio_url && (
        <audio ref={audioRef} src={card.audio_url} loop preload="auto" playsInline style={{ display: 'none' }} />
      )}
      
      {/* Intro Screen - Hide when opened */}
      {!isOpened && (
        <div 
          className="fixed inset-0 flex items-center justify-center sm:bg-gradient-to-b sm:from-gray-100 sm:to-gray-200 z-50"
          data-testid="intro-screen"
        >
          {/* Mobile: Full screen | Desktop: Narrow card with 5px margin */}
          <div className={`relative w-full h-full sm:w-[380px] sm:h-[calc(100vh-10px)] sm:my-[5px] sm:rounded-2xl overflow-hidden sm:shadow-xl transition-all duration-700 ease-out ${isTransitioning ? 'scale-105 opacity-0' : 'scale-100 opacity-100'}`}>
            {/* Background image with parallax effect */}
            <div 
              className={`absolute inset-0 transition-transform duration-700 ${isTransitioning ? 'scale-110' : 'scale-100'}`}
              style={{ backgroundImage: `url(${card.background_image})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
            />
            {/* Overlay with customizable opacity */}
            <div 
              className="absolute inset-0"
              style={{ backgroundColor: `rgba(0,0,0,${card.card_colors?.overlay_opacity || 0.1})` }}
            ></div>
            
            {/* Content with fade and slide up animation */}
            <div 
              className={`relative z-10 h-full flex flex-col items-center justify-center text-center px-6 transition-all duration-700 ${isTransitioning ? 'opacity-0 translate-y-8' : 'opacity-100 translate-y-0'}`}
              style={{ color: card.card_colors?.text_primary || '#4a4a4a' }}
            >
              <p className="text-xs tracking-[0.3em] mb-6 opacity-80 animate-fade-in uppercase" style={{ animationDelay: '0.1s' }}>Walimatulurus</p>
              <h1 className="text-4xl sm:text-5xl mb-3 animate-fade-in" style={{ color: card.secondary_color || '#4a4a4a', fontFamily, animationDelay: '0.2s' }}>
                {card.bride_name}
              </h1>
              <p className="text-2xl my-3 animate-fade-in" style={{ fontFamily, animationDelay: '0.3s' }}>&</p>
              <h1 className="text-4xl sm:text-5xl mb-10 animate-fade-in" style={{ color: card.secondary_color || '#4a4a4a', fontFamily, animationDelay: '0.4s' }}>
                {card.groom_name}
              </h1>
              <Button
                onClick={handleOpen}
                data-testid="open-card-btn"
                disabled={isTransitioning}
                className="hover:opacity-90 text-white rounded-full px-8 py-5 text-sm font-semibold tracking-wider uppercase animate-fade-in hover:scale-105 transition-transform shadow-lg"
                style={{ 
                  backgroundColor: card.card_colors?.onboarding_button_bg || '#FFFFFF',
                  color: card.card_colors?.onboarding_button_text || '#000000',
                  animationDelay: '0.5s' 
                }}
              >
                {isTransitioning ? 'Membuka...' : 'Buka'}
              </Button>
            </div>
          </div>
          
          {/* Transition animation styles */}
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(20px); }
              to { opacity: 1; transform: translateY(0); }
            }
            .animate-fade-in {
              animation: fadeIn 0.6s ease-out forwards;
              opacity: 0;
            }
          `}</style>
        </div>
      )}

      {/* Main Card - Show when opened */}
      {isOpened && (
        <div className="fixed inset-0 sm:bg-gradient-to-b sm:from-gray-100 sm:to-gray-200 flex items-center justify-center" data-testid="public-card-view">
          {/* SEO Meta Tags - noindex for individual cards */}
          <Helmet>
            <title>{getPageTitle()}</title>
            <meta name="robots" content="noindex, nofollow" />
            <meta property="og:title" content={getPageTitle()} />
            <meta property="og:description" content={`Jemputan ke majlis perkahwinan ${card.bride_name} & ${card.groom_name}`} />
            {card.background_image && <meta property="og:image" content={card.background_image} />}
            <meta property="og:type" content="website" />
          </Helmet>
      
      {/* Mobile: Full screen | Desktop: Narrow card with 5px margin */}
      <div className="relative w-full h-full sm:w-[380px] sm:h-[calc(100vh-10px)] sm:my-[5px] bg-white sm:rounded-2xl sm:shadow-xl overflow-hidden flex flex-col">
        {/* Scrollable Content */}
        <div
          className="flex-1 overflow-y-auto scrollbar-hide"
          style={{ backgroundImage: `url(${card.background_image})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'local' }}
        >
          <div 
            className="min-h-full p-5 pt-8 pb-6"
            style={{ backgroundColor: `rgba(0,0,0,${card.card_colors?.overlay_opacity || 0.1})` }}
          >
            <div className="space-y-5 text-center" style={{ color: card.card_colors?.text_primary || '#4a4a4a' }}>
              {/* Modern Sound Bar - Music Player (color follows icon color) */}
              {card.audio_url && (
                <div className="flex justify-center">
                  <button 
                    onClick={toggleAudio} 
                    data-testid="audio-toggle-btn" 
                    className="flex items-center gap-3 bg-white/90 backdrop-blur-md rounded-full px-4 py-2 shadow-lg hover:bg-white transition-all"
                  >
                    {/* Animated Sound Bars - color follows icon/primary color */}
                    <SoundBars isPlaying={audioPlaying} color={card.primary_color || '#4a4a4a'} />
                    
                    {/* Song Title */}
                    <span className="text-sm font-medium max-w-[200px] truncate" style={{ color: card.primary_color || '#4a4a4a' }}>
                      {card.audio_title || 'Background Music'}
                    </span>
                  </button>
                </div>
              )}

              {/* Greeting / Salam in Jawi */}
              <div className="space-y-2">
                <p className="text-lg font-arabic leading-relaxed" dir="rtl">اَلسَّلامُ عَلَيْكُمْ وَرَحْمَةُ اللّٰهِ وَبَرَكَاتُهُ</p>
                <p className="text-xs opacity-80">Salam Sejahtera</p>
                
                {/* Parents Names - Directly below Salam Sejahtera */}
                {displayParent && (
                  <p className="text-sm font-bold uppercase tracking-wider pt-2" style={{ color: card.secondary_color }}>{displayParent}</p>
                )}
                {otherParent && (
                  <p className="text-sm font-bold uppercase tracking-wider" style={{ color: card.secondary_color }}>{otherParent}</p>
                )}
              </div>

              {/* Invitation Text */}
              <div className="px-4">
                <p className="text-xs opacity-90 leading-relaxed">
                  Dengan penuh kesyukuran kehadrat Ilahi, kami mempersilakan Dato'/Datin/Dr/Tuan/Puan/Encik/Cik ke walimatulurus anakanda kesayangan kami
                </p>
              </div>

              <div className="h-px w-16 mx-auto bg-white/40"></div>

              {/* Couple Names */}
              <div className="space-y-3">
                <h1 className="text-2xl sm:text-3xl" style={{ color: card.secondary_color, fontFamily }} data-testid="bride-name">
                  {card.bride_name}
                </h1>
                <p className="text-lg font-light">&</p>
                <h1 className="text-2xl sm:text-3xl" style={{ color: card.secondary_color, fontFamily }} data-testid="groom-name">
                  {card.groom_name}
                </h1>
              </div>

              <div className="h-px w-24 mx-auto bg-white/40"></div>

              {/* Countdown */}
              <Countdown targetDate={card.wedding_date} targetTime={startTime} colors={card.card_colors} />

              <div className="h-px w-24 mx-auto bg-white/40"></div>

              {/* Date Section */}
              <div className="space-y-1">
                <p className="text-xs tracking-widest opacity-80">TARIKH</p>
                <p className="text-lg font-semibold" data-testid="wedding-date">
                  {new Date(card.wedding_date).toLocaleDateString('ms-MY', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </p>
              </div>

              {/* Time Section */}
              <div className="space-y-1">
                <p className="text-xs tracking-widest opacity-80">MASA</p>
                <p className="text-base">
                  {formatTime(startTime)} {card.event_end_time && `- ${formatTime(card.event_end_time)}`}
                </p>
              </div>

              {/* Venue Section */}
              <div className="space-y-1">
                <p className="text-xs tracking-widest opacity-80">TEMPAT</p>
                <p className="text-base font-semibold" data-testid="venue-name">{card.venue_name}</p>
                <p className="text-xs opacity-90 px-4 leading-relaxed">{card.venue_address}</p>
              </div>

              <div className="h-px w-24 mx-auto bg-white/40"></div>

              {/* Aturcara Majlis */}
              {card.aturcara && card.aturcara.length > 0 && (
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                  <p className="text-xs tracking-widest mb-3 opacity-80">ATURCARA MAJLIS</p>
                  <div className="space-y-2 text-sm">
                    {card.aturcara.map((item, idx) => (
                      <div key={idx} className="flex justify-between px-4">
                        <span className="opacity-80">{item.time}</span>
                        <span>{item.activity}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Gallery Carousel */}
              {card.gallery_images && card.gallery_images.length > 0 && (
                <div className="py-2">
                  <p className="text-xs tracking-widest mb-3 opacity-80">GALERI</p>
                  <div className="relative">
                    {/* Main Image */}
                    <img 
                      src={card.gallery_images[galleryIndex]} 
                      alt={`Galeri ${galleryIndex + 1}`} 
                      className="w-full max-w-[280px] mx-auto rounded-xl shadow-lg aspect-square object-cover"
                    />
                    
                    {/* Navigation Arrows - only show if more than 1 image */}
                    {card.gallery_images.length > 1 && (
                      <>
                        <button 
                          onClick={() => setGalleryIndex(prev => prev === 0 ? card.gallery_images.length - 1 : prev - 1)}
                          className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full p-1.5 transition-colors"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </button>
                        <button 
                          onClick={() => setGalleryIndex(prev => prev === card.gallery_images.length - 1 ? 0 : prev + 1)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 text-white rounded-full p-1.5 transition-colors"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </button>
                      </>
                    )}
                    
                    {/* Dots Indicator */}
                    {card.gallery_images.length > 1 && (
                      <div className="flex justify-center gap-1.5 mt-3">
                        {card.gallery_images.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setGalleryIndex(idx)}
                            className={`w-2 h-2 rounded-full transition-all ${idx === galleryIndex ? 'bg-white scale-110' : 'bg-white/40'}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Doa / Closing Message */}
              {card.doa ? (
                <div className="py-3 px-2">
                  <p className="text-xs italic opacity-90 leading-relaxed">"{card.doa}"</p>
                </div>
              ) : (
                <div className="py-3 px-2">
                  <p className="text-xs italic opacity-90 leading-relaxed">
                    "Semoga dengan kehadiran serta iringan doa restu para undangan yang dihormati akan menyerikan lagi majlis kami"
                  </p>
                </div>
              )}

              {/* Guestbook Section - Button for Ucapan with Carousel */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <p className="text-xs tracking-widest mb-2 opacity-80">UCAPAN</p>
                <p className="text-xs opacity-80 mb-3">Tinggalkan ucapan dan doa anda untuk pasangan pengantin</p>
                <Button 
                  onClick={() => setShowGuestbook(true)} 
                  className="rounded-full px-6 text-sm border-0 mb-4"
                  style={{ 
                    backgroundColor: card.card_colors?.button_bg || card.primary_color || '#064E3B',
                    color: card.card_colors?.button_text || '#FFFFFF'
                  }}
                >
                  ✍️ Tulis Ucapan
                </Button>
                
                {/* Guestbook Carousel */}
                {guestbookEntries.length > 0 && (
                  <div className="mt-4">
                    <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide snap-x snap-mandatory">
                      {guestbookEntries.map((entry, idx) => (
                        <div 
                          key={idx} 
                          className="flex-shrink-0 w-48 p-3 rounded-lg snap-start"
                          style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}
                        >
                          <p className="text-xs italic mb-2 line-clamp-3">"{entry.message}"</p>
                          <p className="text-[10px] font-semibold" style={{ color: card.secondary_color }}>— {entry.name}</p>
                        </div>
                      ))}
                    </div>
                    <p className="text-[10px] opacity-50 mt-2">↔ Swipe untuk lihat lebih</p>
                  </div>
                )}
              </div>

              {/* Hashtag & Social Media */}
              {(card.hashtag || (card.social_media && card.social_media.length > 0)) && (
                <div className="py-3">
                  {card.hashtag && (
                    <p className="text-lg" style={{ color: card.secondary_color, fontFamily }}>
                      #{card.hashtag}
                    </p>
                  )}
                  {card.social_media && card.social_media.length > 0 && (
                    <div className="flex justify-center gap-3 mt-2">
                      {card.social_media.map((social, idx) => (
                        <a 
                          key={idx} 
                          href={social.url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="bg-white/20 backdrop-blur-sm rounded-full p-2 hover:bg-white/30 transition-colors"
                        >
                          {getSocialIcon(social.platform)}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Footer */}
              <div className="pt-4">
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Navigation - 5 icons */}
        <div className="bg-white border-t border-gray-100 px-1 py-2 pb-3">
          <div className="flex justify-around items-center">
            {iconVisibility.rsvp && (
              <button onClick={() => { setShowRSVP(true); setRsvpStep('name'); }} data-testid="rsvp-btn" className="flex flex-col items-center gap-1 transition-colors min-w-[50px]" style={{ color: card.primary_color || '#4a4a4a' }}>
                <MessageSquare className="w-5 h-5" />
                <span className="text-[9px] font-medium">RSVP</span>
              </button>
            )}
            
            {/* Hadiah (Gift) */}
            {iconVisibility.gift && (
              <button onClick={() => setShowGift(true)} data-testid="gift-btn" className="flex flex-col items-center gap-1 transition-colors min-w-[50px]" style={{ color: card.primary_color || '#4a4a4a' }}>
                <Gift className="w-5 h-5" />
                <span className="text-[9px] font-medium">Hadiah</span>
              </button>
            )}
            
            {iconVisibility.contact && (
              <button onClick={() => setShowContact(true)} data-testid="contact-btn" className="flex flex-col items-center gap-1 transition-colors min-w-[50px]" style={{ color: card.primary_color || '#4a4a4a' }}>
                <Phone className="w-5 h-5" />
                <span className="text-[9px] font-medium">Hubungi</span>
              </button>
            )}
            
            {iconVisibility.location && (
              <button onClick={() => setShowLocation(true)} data-testid="location-btn" className="flex flex-col items-center gap-1 transition-colors min-w-[50px]" style={{ color: card.primary_color || '#4a4a4a' }}>
                <MapPin className="w-5 h-5" />
                <span className="text-[9px] font-medium">Lokasi</span>
              </button>
            )}
            
            {iconVisibility.calendar && (
              <button onClick={() => setShowCalendar(true)} data-testid="calendar-btn" className="flex flex-col items-center gap-1 transition-colors min-w-[50px]" style={{ color: card.primary_color || '#4a4a4a' }}>
                <Calendar className="w-5 h-5" />
                <span className="text-[9px] font-medium">Kalendar</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* RSVP Dialog - New flow: Name first, then attendance choice */}
      <Dialog open={showRSVP} onOpenChange={(open) => { setShowRSVP(open); if (!open) { setRsvpStep('name'); setRsvpForm({ name: '', phone: '', num_guests: 1, wish_message: '' }); setRsvpAttending(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-playfair text-2xl text-emerald-900">RSVP</DialogTitle>
          </DialogHeader>
          
          {/* Step 1: Ask for name */}
          {rsvpStep === 'name' && (
            <div className="space-y-4 py-4">
              <p className="text-gray-600 text-center">Sila masukkan nama anda</p>
              <div className="space-y-2">
                <Label>Nama Penuh *</Label>
                <Input 
                  value={rsvpForm.name} 
                  onChange={(e) => setRsvpForm({...rsvpForm, name: e.target.value})} 
                  placeholder="Masukkan nama penuh anda" 
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleRSVPNameSubmit();
                    }
                  }}
                />
              </div>
              <Button 
                onClick={handleRSVPNameSubmit} 
                className="w-full bg-emerald-900 text-white hover:bg-emerald-800 rounded-full"
                disabled={!rsvpForm.name.trim()}
              >
                Seterusnya
              </Button>
            </div>
          )}

          {/* Step 2: Ask attendance */}
          {rsvpStep === 'choice' && (
            <div className="space-y-4 text-center py-4">
              <div className="bg-emerald-50 p-3 rounded-lg">
                <p className="text-emerald-800 font-medium">Hai, {rsvpForm.name}!</p>
              </div>
              <p className="text-gray-600">Adakah anda akan hadir ke majlis ini?</p>
              <div className="flex gap-4 justify-center">
                <Button onClick={() => handleRSVPAttendance(true)} className="bg-emerald-900 text-white hover:bg-emerald-800 rounded-full px-8">
                  ✓ Hadir
                </Button>
                <Button onClick={() => handleRSVPAttendance(false)} variant="outline" className="rounded-full px-8">
                  ✕ Tidak Hadir
                </Button>
              </div>
            </div>
          )}

          {/* Step 3a: If attending - fill in details */}
          {rsvpStep === 'form' && (
            <form onSubmit={handleRSVPSubmit} className="space-y-4">
              <div className="bg-emerald-50 p-3 rounded-lg text-center">
                <p className="text-emerald-800 font-medium">✓ Terima kasih {rsvpForm.name} kerana sudi hadir!</p>
              </div>
              <div className="space-y-2">
                <Label>No. Telefon *</Label>
                <Input value={rsvpForm.phone} onChange={(e) => setRsvpForm({...rsvpForm, phone: e.target.value})} placeholder="012-3456789" required />
              </div>
              <div className="space-y-2">
                <Label>Bilangan Tetamu</Label>
                <div className="flex items-center gap-2">
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setRsvpForm({...rsvpForm, num_guests: Math.max(1, rsvpForm.num_guests - 1)})}
                    className="w-10 h-10 text-lg font-bold"
                  >
                    -
                  </Button>
                  <Input 
                    type="text" 
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={rsvpForm.num_guests} 
                    onChange={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      setRsvpForm({...rsvpForm, num_guests: Math.min(20, Math.max(1, val))});
                    }} 
                    className="w-20 text-center text-lg font-semibold"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    size="sm"
                    onClick={() => setRsvpForm({...rsvpForm, num_guests: Math.min(20, rsvpForm.num_guests + 1)})}
                    className="w-10 h-10 text-lg font-bold"
                  >
                    +
                  </Button>
                </div>
              </div>
              <Button type="submit" className="w-full bg-emerald-900 text-white hover:bg-emerald-800 rounded-full" disabled={rsvpSubmitting}>
                {rsvpSubmitting ? 'Menghantar...' : 'Hantar RSVP'}
              </Button>
            </form>
          )}

          {/* Step 3b: If not attending - optional wish message */}
          {rsvpStep === 'notAttending' && (
            <form onSubmit={handleNotAttendingSubmit} className="space-y-4">
              <div className="bg-amber-50 p-3 rounded-lg text-center">
                <p className="text-amber-800 font-medium">Terima kasih {rsvpForm.name} atas maklum balas anda</p>
              </div>
              <div className="space-y-2">
                <Label>Ucapan (Opsional)</Label>
                <Textarea 
                  value={rsvpForm.wish_message} 
                  onChange={(e) => setRsvpForm({...rsvpForm, wish_message: e.target.value})} 
                  placeholder="Tinggalkan ucapan anda untuk pasangan pengantin..." 
                  rows={3}
                />
              </div>
              <Button type="submit" className="w-full bg-emerald-900 text-white hover:bg-emerald-800 rounded-full" disabled={rsvpSubmitting}>
                {rsvpSubmitting ? 'Menghantar...' : 'Hantar'}
              </Button>
            </form>
          )}

          {rsvpStep === 'done' && (
            <div className="text-center py-8">
              <div className="text-4xl mb-4">✓</div>
              <p className="text-emerald-800 font-medium">Terima kasih!</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Guestbook Dialog - Name and Message only */}
      <Dialog open={showGuestbook} onOpenChange={setShowGuestbook}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-playfair text-2xl text-emerald-900 flex items-center gap-2">
              <BookOpen className="w-6 h-6" /> Ucapan
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleGuestbookSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nama Anda *</Label>
              <Input value={guestbookForm.name} onChange={(e) => setGuestbookForm({...guestbookForm, name: e.target.value})} placeholder="Masukkan nama anda" required />
            </div>
            <div className="space-y-2">
              <Label>Ucapan & Doa *</Label>
              <Textarea value={guestbookForm.message} onChange={(e) => setGuestbookForm({...guestbookForm, message: e.target.value})} placeholder="Tahniah dan selamat pengantin baru..." rows={4} required />
            </div>
            <Button type="submit" className="w-full bg-emerald-900 text-white hover:bg-emerald-800 rounded-full" disabled={guestbookSubmitting}>
              {guestbookSubmitting ? 'Menghantar...' : 'Hantar Ucapan'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Gift Dialog */}
      <Dialog open={showGift} onOpenChange={setShowGift}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-playfair text-2xl text-amber-600 flex items-center gap-2">
              <Gift className="w-6 h-6" /> Hadiah
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {card.gift_qr_image && (
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-3">Imbas QR untuk sumbangan:</p>
                <img src={card.gift_qr_image} alt="QR Code" className="mx-auto max-w-[200px] rounded-lg border" />
              </div>
            )}
            
            {card.money_gift_bank && (
              <div className="p-4 bg-amber-50 rounded-lg space-y-2">
                <p className="text-sm text-gray-600 mb-2">Atau transfer ke akaun:</p>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Bank:</span>
                  <span className="font-semibold">{card.money_gift_bank}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">No. Akaun:</span>
                  <span className="font-semibold">{card.money_gift_account}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Nama:</span>
                  <span className="font-semibold">{card.money_gift_name}</span>
                </div>
                <Button onClick={() => copyToClipboard(card.money_gift_account)} variant="outline" className="w-full mt-2">
                  {copied ? '✓ Disalin!' : 'Salin No. Akaun'}
                </Button>
              </div>
            )}
            
            {card.gift_wishlist && card.gift_wishlist.length > 0 && (
              <div className="border-t pt-4">
                <p className="text-sm text-gray-600 mb-3">Senarai Hajat:</p>
                <div className="space-y-2">
                  {card.gift_wishlist.map((item, idx) => {
                    const isReserved = !!item.reserved_by;
                    const isShopee = item.link?.includes('shopee');
                    const isLazada = item.link?.includes('lazada');
                    
                    return (
                      <div key={idx} className={`p-3 rounded-lg ${isReserved ? 'bg-gray-200 opacity-60' : 'bg-gray-50'}`}>
                        <div className="flex items-start gap-3">
                          {/* Item image */}
                          {item.image && (
                            <img src={item.image} alt={item.item} className="w-16 h-16 object-cover rounded border flex-shrink-0" />
                          )}
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className={`font-medium ${isReserved ? 'line-through text-gray-500' : ''}`}>{item.item}</span>
                              <div className="flex items-center gap-2">
                                {item.link && !isReserved && (
                                  <a href={item.link} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 px-2 py-1 rounded bg-white border hover:bg-gray-50">
                                    {isShopee ? (
                                      <span className="text-orange-500 font-bold text-xs">🛒 Shopee</span>
                                    ) : isLazada ? (
                                      <span className="text-blue-600 font-bold text-xs">🛍️ Lazada</span>
                                    ) : (
                                      <ExternalLink className="w-4 h-4 text-amber-600" />
                                    )}
                                  </a>
                                )}
                              </div>
                            </div>
                            
                            {isReserved ? (
                              <p className="text-xs text-gray-500 mt-2">✓ Sudah ditempah oleh {item.reserved_by}</p>
                            ) : (
                              <div className="mt-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="w-full text-xs"
                                  onClick={async () => {
                                    const name = prompt('Masukkan nama anda untuk tempah item ini:');
                                    if (name && name.trim()) {
                                      try {
                                        await axios.post(`${BACKEND_URL}/api/wishlist/${slug}/reserve`, {
                                      item_index: idx,
                                      reserved_by: name.trim()
                                    });
                                    toast.success('Item berjaya ditempah!');
                                    fetchCard(); // Refresh card data
                                  } catch (err) {
                                    toast.error(err.response?.data?.detail || 'Gagal menempah item');
                                  }
                                }
                              }}
                            >
                              🎁 Saya nak bagi hadiah ini
                            </Button>
                          </div>
                        )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {!card.gift_qr_image && !card.money_gift_bank && (!card.gift_wishlist || card.gift_wishlist.length === 0) && (
              <p className="text-center text-gray-500">Tiada maklumat hadiah dikemaskini.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog open={showContact} onOpenChange={setShowContact}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-playfair text-2xl text-emerald-900">Senarai Boleh Hubungi</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {card.contacts && card.contacts.length > 0 ? (
              card.contacts.map((contact, idx) => (
                <div key={idx} className="p-4 bg-amber-50 rounded-lg space-y-2">
                  <div>
                    <p className="text-xs text-gray-500">{contact.role}</p>
                    <p className="font-semibold text-lg">{contact.name}</p>
                  </div>
                  <div className="flex gap-2">
                    <a href={`tel:${contact.phone}`} className="flex-1">
                      <Button className="w-full bg-emerald-900 text-white hover:bg-emerald-800 rounded-full" size="sm">
                        <Phone className="w-4 h-4 mr-2" /> Telefon
                      </Button>
                    </a>
                    <Button variant="outline" className="flex-1 rounded-full" size="sm" onClick={() => openWhatsApp(contact.phone, contact.name)}>
                      <MessageSquare className="w-4 h-4 mr-2" /> WhatsApp
                    </Button>
                  </div>
                </div>
              ))
            ) : card.contact_name && card.contact_phone ? (
              <div className="p-4 bg-amber-50 rounded-lg space-y-3">
                <div>
                  <p className="text-sm text-gray-600">Nama:</p>
                  <p className="font-semibold text-lg">{card.contact_name}</p>
                </div>
                <div className="flex gap-2">
                  <a href={`tel:${card.contact_phone}`} className="flex-1">
                    <Button className="w-full bg-emerald-900 text-white hover:bg-emerald-800 rounded-full">
                      <Phone className="w-4 h-4 mr-2" /> Telefon
                    </Button>
                  </a>
                  <Button variant="outline" className="flex-1 rounded-full" onClick={() => openWhatsApp(card.contact_phone, card.contact_name)}>
                    <MessageSquare className="w-4 h-4 mr-2" /> WhatsApp
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-center text-gray-500">Tiada maklumat hubungi.</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Location Dialog */}
      <Dialog open={showLocation} onOpenChange={setShowLocation}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-playfair text-2xl text-emerald-900">Lokasi Majlis</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 rounded-lg">
              <p className="font-semibold">{card.venue_name}</p>
              <p className="text-sm text-gray-600 mt-1">{card.venue_address}</p>
            </div>
            <p className="text-sm text-gray-600 text-center">Pilih aplikasi navigasi:</p>
            <div className="flex gap-3">
              <Button onClick={() => handleOpenMap('waze')} className="flex-1 bg-[#33CCFF] hover:bg-[#2BB8E8] text-white rounded-full">
                Waze
              </Button>
              <Button onClick={() => handleOpenMap('google')} className="flex-1 bg-[#4285F4] hover:bg-[#3574D8] text-white rounded-full">
                <MapPin className="w-5 h-5 mr-2" /> Google Maps
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Calendar Dialog */}
      <Dialog open={showCalendar} onOpenChange={setShowCalendar}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-playfair text-2xl text-emerald-900">Tambah ke Kalendar</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-amber-50 rounded-lg text-center">
              <p className="font-semibold">
                {new Date(card.wedding_date).toLocaleDateString('ms-MY', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              <p className="text-sm text-gray-600 mt-1">{formatTime(startTime)} {card.event_end_time && `- ${formatTime(card.event_end_time)}`}</p>
            </div>
            <p className="text-sm text-gray-600 text-center">Pilih aplikasi kalendar:</p>
            <div className="flex flex-col gap-3">
              <Button onClick={() => handleAddToCalendar('google')} className="w-full bg-[#4285F4] hover:bg-[#3574D8] text-white rounded-full">
                <Calendar className="w-5 h-5 mr-2" /> Google Calendar
              </Button>
              <Button onClick={() => handleAddToCalendar('ics')} variant="outline" className="w-full rounded-full">
                <Calendar className="w-5 h-5 mr-2" /> Muat Turun (.ics)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <style jsx>{`
        .scrollbar-hide::-webkit-scrollbar { display: none; }
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .safe-area-bottom { padding-bottom: max(0.5rem, env(safe-area-inset-bottom)); }
      `}</style>
    </div>
      )}
    </>
  );
}
